import React from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Product-page FAQ block targeting Google's "People Also Ask" search
 * intent (per the SEO playbook). Each question is a real query
 * British buyers type into Google. Visible content matches the
 * FAQPage JSON-LD 1:1 — Google's core requirement for FAQ rich
 * snippets to appear.
 *
 * MHRA/ASA-compliant copy: no medical claims, no "cure", no "treat".
 */

const FAQS = [
  {
    q: 'Where is IIY Phytoventra BCM-95® shipped from?',
    a: 'All orders are dispatched directly from our UK inventory, ensuring fast domestic transit across Great Britain and Northern Ireland via standard tracked delivery. Typical delivery is 2-3 working days.',
  },
  {
    q: 'Is this the authentic patented BCM-95® / Curcugreen® extract?',
    a: 'Yes. IIY Phytoventra works directly with Arjuna Natural Pvt. Ltd. — the patent-holder of the BCM-95® / Curcugreen® formulation — and our Scientific Advisor Dr Benny Antony, PhD is the co-developer of the BCM-95® patent. Every bottle is traceable to the official manufacturing batch with full GMP-certified provenance.',
  },
  {
    q: 'How much curcumin is actually in each serving?',
    a: 'A 2-capsule daily serving delivers 1000 mg of Purified Turmeric Extract Curcugreen®, of which 650 mg is standardised curcumin. The remainder is the patented turmeric essential oil fraction (turmerones) that enhances absorption — no piperine, no synthetic carriers.',
  },
  {
    q: 'How long until I notice anything?',
    a: 'Published curcumin trials typically run for 8-12 weeks of consistent daily use, and most users build curcumin into a long-term active-living routine alongside vitamin D and omega-3. Consistency matters more than dose.',
  },
  {
    q: 'Is BCM-95® better than regular turmeric capsules?',
    a: 'Standard turmeric capsules typically contain 2-5% curcuminoids by weight, and curcumin from plain extracts is notoriously poorly absorbed. BCM-95® uses a patented combination of standardised curcuminoids reintegrated with turmeric\'s own essential oils, which published pharmacokinetic studies (Antony et al., 2008) show provides substantially higher relative oral bioavailability compared with standard 95% curcumin extract.',
  },
  {
    q: 'Is BCM-95® suitable for vegans and people with allergies?',
    a: 'Yes. 100% vegan, vegetable-cellulose capsules, allergen-free (no gluten, soya, dairy, or nuts), no artificial colours, manufactured under GMP at a facility powered entirely by solar energy.',
  },
  {
    q: 'Can I get a UK VAT invoice for a business purchase?',
    a: 'Yes. Tick "I\'m purchasing for a UK VAT-registered business" at checkout and add your Business Name + VAT Number. You\'ll receive an itemised Style B invoice with Net + VAT (20%) + Total breakdown, suitable for HMRC reporting.',
  },
  {
    q: 'How does your pricing work — is it cheaper to buy more?',
    a: 'Yes. All prices are inclusive of UK VAT and free standard delivery: 1 bottle £37.49, 2 bottles £70.00 (£35.00 each), 3 bottles £93.00 (£31.00 each). 4 or more bottles are charged at £31.00 per bottle. Your total saving is shown automatically at checkout.',
  },
];

export default function ProductFAQ() {
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <section className="my-16" data-testid="product-faq">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
      />
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-2">
        Frequently asked
      </div>
      <h2 className="text-2xl md:text-3xl font-bold font-heading text-emerald-900 mb-6 leading-tight">
        BCM-95<sup>&reg;</sup> &amp; BCM-95® — your questions answered
      </h2>
      <div className="space-y-2">
        {FAQS.map((f, i) => (
          <details
            key={i}
            className="group rounded-xl border border-emerald-700/15 bg-white px-5 py-4 open:bg-emerald-50/40 transition-colors"
            data-testid={`product-faq-${i}`}
          >
            <summary className="cursor-pointer font-bold text-emerald-900 list-none flex items-start justify-between gap-3">
              <span className="text-[15px] leading-snug">{f.q}</span>
              <ChevronDown className="w-4 h-4 shrink-0 text-emerald-700 group-open:rotate-180 transition-transform" />
            </summary>
            <p className="text-slate-700 mt-3 leading-relaxed text-[14.5px]">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
